import { PlayIntegrity } from '@capacitor-community/play-integrity';
import { Capacitor } from '@capacitor/core';
import type { SupabaseClient } from '@supabase/supabase-js';

// Google Cloud Project Configuration
// Get this from: https://console.cloud.google.com/home/dashboard?project=blissnet
// Look for "Project number" at the top of the dashboard (12-digit number)
const GOOGLE_CLOUD_PROJECT_NUMBER = 25842299623;

/**
 * Service for Google Play Integrity API
 * Detects bots, emulators, and tampered devices
 *
 * @version V314.7
 */

interface IntegrityResult {
  token: string | null;
  error: string | null;
  timestamp: number;
  // V314.7: Non-PII diagnostics (only when pi_debug enabled)
  diagnostics?: {
    platform: string;
    isNative: boolean;
    errorCode?: string;
    buildType?: string; // 'debug' | 'release' | 'unknown'
  };
}

interface IntegrityVerdict {
  appRecognitionVerdict: string | null;
  deviceRecognitionVerdict: string[];
  appLicensingVerdict: string | null;
}

interface VerificationResult {
  ok: boolean;
  verdict: IntegrityVerdict;
  reasons: string[];
  requestId?: string;
  responseTimeMs?: number;
  packageName?: string;
}

export class IntegrityService {
  private static readonly PACKAGE_NAME = 'com.blissnet.app';
  private static readonly TIMEOUT_MS = 5000; // 5 seconds
  private static readonly MAX_RETRIES = 2;

  /**
   * Generate cryptographic nonce from user email
   */
  static async generateNonce(email: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(`${email}-${Date.now()}-${Math.random()}`);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return btoa(hashArray.map(b => String.fromCharCode(b)).join(''));
  }

  /**
   * Request integrity token from Google Play
   * V314.7: Enhanced with non-PII diagnostics when pi_debug enabled
   * V315.9 TASK B: Enhanced logging and error handling for Android native token bridge
   */
  static async requestIntegrityToken(nonce: string): Promise<IntegrityResult> {
    const timestamp = Date.now();
    const platform = Capacitor.getPlatform();
    const isNative = Capacitor.isNativePlatform();
    const piDebug = typeof window !== 'undefined' && localStorage.getItem('pi_debug') === '1';

    // V314.7: Build diagnostics object (non-PII only)
    const diagnostics = piDebug ? {
      platform,
      isNative,
      buildType: 'unknown' as 'debug' | 'release' | 'unknown'
    } : undefined;

    // Only run on Android
    if (!isNative || platform !== 'android') {
      const error = 'NOT_ANDROID';
      if (piDebug || import.meta.env.DEV) {
        console.log('[V315.9 IntegrityService] Not on Android platform - PI requires native Android');
        console.log('[V315.9 IntegrityService] Platform:', platform, 'IsNative:', isNative);
      }
      return { token: null, error, timestamp, diagnostics };
    }

    try {
      // V315.9 TASK B: Always log start on Android (production-safe)
      console.log('[V315.9 IntegrityService] Requesting token...', {
        nonce: nonce.substring(0, 20) + '...',
        platform,
        projectNumber: GOOGLE_CLOUD_PROJECT_NUMBER
      });

      // Request with timeout
      const tokenPromise = PlayIntegrity.requestIntegrityToken({
        nonce,
        googleCloudProjectNumber: GOOGLE_CLOUD_PROJECT_NUMBER
      });
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('TIMEOUT')), this.TIMEOUT_MS)
      );

      const result = await Promise.race([tokenPromise, timeoutPromise]);

      if (!result || !result.token) {
        throw new Error('EMPTY_RESPONSE');
      }

      // V315.9 TASK B: Always log success on Android (production-safe, no token value)
      console.log('[V315.9 IntegrityService] ✅ Token received (length:', result.token.length, ')');
      return { token: result.token, error: null, timestamp, diagnostics };

    } catch (error) {
      const errCode = error instanceof Error ? error.message : 'UNKNOWN_ERROR';

      // V315.9 TASK B: Always log failure on Android (production-safe)
      console.log('[V315.9 IntegrityService] ❌ Token request failed:', errCode);

      // V315.9 TASK B: Enhanced diagnostics (always show in DEV or pi_debug)
      if (piDebug || import.meta.env.DEV) {
        console.log('[V315.9 IntegrityService] Common causes:');
        console.log('  - Debug build (PI requires release build signed with Play Store key)');
        console.log('  - App not installed from Play Store (sideloaded APK)');
        console.log('  - Play Integrity API not enabled in Google Cloud Console');
        console.log('  - Wrong package name (com.blissnet.app) or cloud project number');
        console.log('  - Device not Google certified');
        console.log('  - Play Services not available or outdated');
      }

      return {
        token: null,
        error: errCode,
        timestamp,
        diagnostics: piDebug ? { ...diagnostics!, errorCode: errCode } : undefined
      };
    }
  }

  /**
   * Request token with retry logic
   */
  static async requestIntegrityTokenWithRetry(nonce: string): Promise<IntegrityResult> {
    for (let attempt = 1; attempt <= this.MAX_RETRIES; attempt++) {
      const result = await this.requestIntegrityToken(nonce);
      
      if (result.token) {
        console.log(`[IntegrityService] Success on attempt ${attempt}/${this.MAX_RETRIES}`);
        return result;
      }

      if (attempt < this.MAX_RETRIES) {
        const backoffMs = Math.min(1000 * Math.pow(2, attempt - 1), 3000);
        console.log(`[IntegrityService] Retry ${attempt + 1} after ${backoffMs}ms`);
        await new Promise(resolve => setTimeout(resolve, backoffMs));
      }
    }

    return {
      token: null,
      error: 'MAX_RETRIES_EXCEEDED',
      timestamp: Date.now()
    };
  }

  /**
   * Verify token with backend Edge Function
   */
  static async verifyIntegrityToken(
    token: string,
    nonce: string,
    supabase: SupabaseClient
  ): Promise<VerificationResult> {
    try {
      console.log('[IntegrityService] Calling Edge Function...');

      const { data, error } = await supabase.functions.invoke('verify-play-integrity', {
        body: {
          integrityToken: token,
          nonce: nonce
        }
      });

      if (error) {
        console.error('[IntegrityService] Edge Function error:', error);
        throw error;
      }

      console.log('[IntegrityService] Verification result:', data);
      return data as VerificationResult;

    } catch (error) {
      console.error('[IntegrityService] Verification failed:', error);
      // Return graceful failure - don't block user
      return {
        ok: false,
        verdict: {
          appRecognitionVerdict: null,
          deviceRecognitionVerdict: [],
          appLicensingVerdict: null
        },
        reasons: ['verification_error']
      };
    }
  }
}

export type { IntegrityResult, IntegrityVerdict, VerificationResult };

